import React, { useRef, useState, useCallback, useEffect } from 'react';
import { showNotification } from './NotificationManager';

/**
 * MicDictationButton
 *
 * A small, reusable mic button that dictates speech into ANY text field using
 * the browser Web Speech API. It is intentionally "dumb": it captures final
 * speech chunks and hands them back via onAppend(chunk) so the parent can merge
 * them into whatever state it owns (a note, a follow-up, a calendar note, etc.).
 *
 * This is the generic sibling of VoiceListingRecorder - that component pipes the
 * transcript into AI listing extraction; this one just turns speech into text so
 * agents can dictate notes/tasks anywhere in the CRM.
 *
 * Renders nothing when the browser has no SpeechRecognition (e.g. Firefox); on
 * mobile the keyboard's own mic still works, so the field stays fully usable.
 */

const SpeechRecognition =
    typeof window !== 'undefined'
        ? window.SpeechRecognition || window.webkitSpeechRecognition
        : null;

export const speechSupported = !!SpeechRecognition;

const MicDictationButton = ({
    onAppend,
    onInterim,
    disabled = false,
    size = 38,
    title = 'Dictate',
    style = {},
}) => {
    const [recording, setRecording] = useState(false);
    const recognitionRef = useRef(null);
    const shouldRestartRef = useRef(false);
    // Keep the latest callbacks in refs so the long-lived recognition session
    // always calls the current handlers without needing to restart.
    const onAppendRef = useRef(onAppend);
    const onInterimRef = useRef(onInterim);
    useEffect(() => { onAppendRef.current = onAppend; }, [onAppend]);
    useEffect(() => { onInterimRef.current = onInterim; }, [onInterim]);

    const stop = useCallback(() => {
        shouldRestartRef.current = false;
        setRecording(false);
        if (onInterimRef.current) onInterimRef.current('');
        const rec = recognitionRef.current;
        if (rec) { try { rec.stop(); } catch (_) { /* noop */ } }
    }, []);

    useEffect(() => () => {
        shouldRestartRef.current = false;
        const rec = recognitionRef.current;
        if (rec) { try { rec.stop(); } catch (_) { /* noop */ } }
    }, []);

    const start = useCallback(() => {
        if (!speechSupported) return;
        const rec = new SpeechRecognition();
        rec.lang = (typeof navigator !== 'undefined' && navigator.language) || 'en-US';
        rec.continuous = true;
        rec.interimResults = true;

        rec.onresult = (event) => {
            let interimText = '';
            for (let i = event.resultIndex; i < event.results.length; i++) {
                const chunk = event.results[i][0].transcript;
                if (event.results[i].isFinal) {
                    const clean = chunk.trim();
                    if (clean && onAppendRef.current) onAppendRef.current(clean);
                } else {
                    interimText += chunk;
                }
            }
            if (onInterimRef.current) onInterimRef.current(interimText.trim());
        };

        rec.onerror = (event) => {
            if (event.error === 'no-speech' || event.error === 'aborted') return;
            if (event.error === 'not-allowed' || event.error === 'service-not-allowed') {
                shouldRestartRef.current = false;
                setRecording(false);
                if (onInterimRef.current) onInterimRef.current('');
                showNotification('Microphone access was blocked. Allow mic access or type instead.', 'error');
                return;
            }
            console.warn('[MicDictationButton] speech error:', event.error);
        };

        // Chrome stops recognition on silence; restart while the user is still dictating.
        rec.onend = () => {
            if (shouldRestartRef.current) {
                try { rec.start(); } catch (_) { /* noop */ }
            }
        };

        recognitionRef.current = rec;
        shouldRestartRef.current = true;
        try {
            rec.start();
            setRecording(true);
        } catch (_) {
            shouldRestartRef.current = false;
            setRecording(false);
            showNotification('Could not start recording. Type instead.', 'error');
        }
    }, []);

    if (!speechSupported) return null;

    return (
        <button
            type="button"
            onClick={recording ? stop : start}
            disabled={disabled}
            title={recording ? 'Stop dictation' : title}
            aria-label={recording ? 'Stop dictation' : title}
            aria-pressed={recording}
            style={{
                width: size,
                height: size,
                minWidth: size,
                borderRadius: '50%',
                border: 'none',
                cursor: disabled ? 'not-allowed' : 'pointer',
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: '#fff',
                background: recording ? '#dc2626' : '#11575C',
                boxShadow: recording ? '0 0 0 6px rgba(220,38,38,0.18)' : '0 2px 8px rgba(17,87,92,0.25)',
                transition: 'box-shadow 0.2s, background 0.2s',
                opacity: disabled ? 0.6 : 1,
                ...style,
            }}
        >
            <i className={`fas ${recording ? 'fa-stop' : 'fa-microphone'}`} style={{ fontSize: size * 0.42 }} />
        </button>
    );
};

/** Append a dictated chunk to existing text with sensible spacing. */
export const appendDictation = (prev, chunk) => {
    const base = (prev || '').replace(/\s+$/, '');
    if (!base) return chunk;
    return `${base} ${chunk}`;
};

export default MicDictationButton;
